import { useToggle } from '@dfl/hook-utils';
import { ConfirmDialog, EditRowActions } from '@dfl/mui-admin-layout';
import { DFLError } from '@dfl/react-security';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CreditScoreIcon from '@mui/icons-material/CreditScore';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import { CircularProgress, IconButton, Stack } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslationLanguage } from 'contexts/TranslationContext';
import { CAMPAIGN_TYPE_ENUM } from 'modules/campaigns/constants';
import { CAMPAIGN_LIST_KEY, CAMPAIGN_ONE_KEY } from 'modules/campaigns/constants/common/campaigns.queries';
import { CAMPAIGN_STATUS_ENUM } from 'modules/campaigns/constants/common/status.enum';
import { useDeleteCampaign } from 'modules/campaigns/hooks/useDeleteCampaign';
import { ICampaign } from 'modules/campaigns/interfaces';
import CampaignPaymentService from 'modules/campaigns/services/campaign-payment.service';
import { DownloadInvoiceButton } from 'modules/common/components/DownloadInvoiceButton';
import { INVOICE_TYPE } from 'modules/common/constants/invoice.enums';
import { useDownloadInvoice } from 'modules/common/hooks/useDownloadInvoice';
import { usePaymentMapError } from 'modules/subscriptions/hooks/usePaymentMapError';
import { memo, useCallback, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

type Props = {
  rowId: string;
  record: ICampaign;
};

const CampaignRowActions = ({ rowId, record }: Props) => {
  const { t } = useTranslation('campaigns');
  const { isOpen: isOpenDelete, onClose: onCloseDelete, setOpen: setOpenDelete } = useToggle();
  const { mutate, isLoading: isDeleting, error } = useDeleteCampaign(rowId, record?.type, onCloseDelete);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: locale } = useTranslationLanguage();
  const { downloadInvoice, isDownloading } = useDownloadInvoice(INVOICE_TYPE.CAMPAIGN);

  const handleEdit = useCallback(() => {
    navigate(`/campaigns/${(record.type as CAMPAIGN_TYPE_ENUM).toLowerCase()}/details/${rowId}/general`);
  }, [navigate, rowId]);

  const handlePay = useCallback(() => {
    navigate(`/campaigns/${(record.type as CAMPAIGN_TYPE_ENUM).toLowerCase()}/pay/${rowId}`);
  }, [navigate, rowId, record.type]);

  const {
    mutate: checkPayment,
    error: checkPaymentError,
    isLoading: isCheckPayment,
  } = useMutation((data: ICampaign) => CampaignPaymentService.check(data, data.type as CAMPAIGN_TYPE_ENUM), {
    onSuccess: (response) => {
      if (response?.status === CAMPAIGN_STATUS_ENUM.WAITING_PAY) {
        toast.success(t('waitingPaymentVerification'));
      } else if (response?.status === CAMPAIGN_STATUS_ENUM.PAID && record.status !== CAMPAIGN_STATUS_ENUM.PAID) {
        queryClient.invalidateQueries([locale, CAMPAIGN_LIST_KEY]);
        queryClient.invalidateQueries([locale, record?._id, CAMPAIGN_ONE_KEY]);
        toast.success(t('paidVerification'));
      }
    },
  });

  const mappedPaymentError = usePaymentMapError((checkPaymentError || error) as DFLError);

  useEffect(() => {
    if (mappedPaymentError) {
      toast.error(mappedPaymentError?.message);
    }
  }, [mappedPaymentError]);

  const handleCheck = useCallback(() => {
    checkPayment(record);
  }, [record]);

  const handleDelete = useCallback(() => {
    setOpenDelete(true);
  }, [setOpenDelete]);

  const handleDownload = useCallback(() => {
    if (record.code) {
      downloadInvoice(record.code);
    }
  }, [record._id, downloadInvoice]);

  if (!record.status || record.status === CAMPAIGN_STATUS_ENUM.CREATED) {
    return (
      <>
        <ConfirmDialog
          title={t('common:deleteConfirmation.title')}
          confirmationMessage={t('common:deleteConfirmation.confirmation')}
          confirmButtonText={t('common:confirm')}
          onConfirm={mutate}
          onClose={onCloseDelete}
          open={isOpenDelete}
          isLoading={isDeleting}
        />
        <Stack direction='row' spacing={1} alignItems={'center'} justifyContent={'flex-end'}>
          <EditRowActions onClick={handleEdit} />
          <IconButton title={t('common:delete')}>
            <DeleteIcon
              sx={{
                fill: 'darkred',
              }}
              onClick={handleDelete}
            />
          </IconButton>
        </Stack>
      </>
    );
  }

  if (!record.status || record.status === CAMPAIGN_STATUS_ENUM.REVIEW) {
    return (
      <>
        <ConfirmDialog
          title={t('common:deleteConfirmation.title')}
          confirmationMessage={t('common:deleteConfirmation.confirmation')}
          confirmButtonText={t('common:confirm')}
          onConfirm={mutate}
          onClose={onCloseDelete}
          open={isOpenDelete}
          isLoading={isDeleting}
        />
        <Stack direction='row' spacing={1} alignItems={'center'} justifyContent={'flex-end'}>
          <EditRowActions onClick={handleEdit} />
          <IconButton title={t('common:delete')}>
            <DeleteIcon
              sx={{
                fill: 'darkred',
              }}
              onClick={handleDelete}
            />
          </IconButton>
        </Stack>
      </>
    );
  }

  if (record.status === CAMPAIGN_STATUS_ENUM.ACCEPTED || record.status === CAMPAIGN_STATUS_ENUM.WAITING_PAY) {
    return (
      <>
        <Stack direction='row' spacing={1} alignItems={'center'} justifyContent={'flex-end'}>
          <EditRowActions onClick={handleEdit} />
          <IconButton title={t('pay')} onClick={handlePay} color={'primary'}>
            <AttachMoneyIcon />
          </IconButton>
          {record.status === CAMPAIGN_STATUS_ENUM.WAITING_PAY && (
            <>
              <IconButton title={t('checkPayment')} onClick={handleCheck} color={'success'} disabled={isCheckPayment}>
                {isCheckPayment ? <CircularProgress color='success' size={16} /> : <CreditScoreIcon />}
              </IconButton>
            </>
          )}
        </Stack>
      </>
    );
  }

  if (record.status === CAMPAIGN_STATUS_ENUM.PAID) {
    return (
      <>
        <Stack direction='row' spacing={1} alignItems={'center'} justifyContent={'flex-end'}>
          <EditRowActions onClick={handleEdit} />
          <DownloadInvoiceButton onClick={handleDownload} isLoading={isDownloading} iconOnly />
        </Stack>
      </>
    );
  }

  return (
    <Stack direction='row' spacing={1} alignItems={'center'} justifyContent={'flex-end'}>
      <EditRowActions onClick={handleEdit} />
    </Stack>
  );
};

export default memo(CampaignRowActions);
